import React, { useState } from 'react';
import { Sun, Moon, Volume2, Sparkles, Sliders, ShieldAlert, Play, Pause, CheckCircle2, RotateCcw } from 'lucide-react';

export const SensoryRoomModule: React.FC = () => {
  const [lightPreset, setLightPreset] = useState<'calm_blue' | 'warm_amber' | 'nature_green' | 'soft_violet'>('calm_blue');
  const [soundscape, setSoundscape] = useState<'ocean' | 'rain' | 'white_noise' | 'forest'>('ocean');
  const [isPlaying, setIsPlaying] = useState(true);
  const [calmingTimer, setCalmingTimer] = useState(15); // minutes

  const presets = [
    { id: 'calm_blue', name: 'Deep Ocean Blue Calm', bg: 'bg-blue-900 text-blue-100 border-blue-600', colorHex: '#1e3a8a' },
    { id: 'warm_amber', name: 'Warm Sunset Amber', bg: 'bg-amber-900 text-amber-100 border-amber-600', colorHex: '#78350f' },
    { id: 'nature_green', name: 'Forest Therapy Green', bg: 'bg-emerald-900 text-emerald-100 border-emerald-600', colorHex: '#064e3b' },
    { id: 'soft_violet', name: 'Snoezelen Lavender', bg: 'bg-purple-900 text-purple-100 border-purple-600', colorHex: '#581c87' },
  ];

  return (
    <div className="p-6 space-y-6 bg-slate-900 text-white rounded-2xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 text-purple-300 text-xs font-bold rounded-full border border-purple-500/30">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" /> Module 17: Sensory Room & Snoezelen Environment
          </div>
          <h2 className="text-2xl font-black mt-2">Sensory Overstimulation & Environmental Control</h2>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setIsPlaying(!isPlaying)}
            className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow transition ${isPlaying ? 'bg-amber-500 text-slate-950' : 'bg-emerald-500 text-white'}`}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {isPlaying ? 'Pause Ambient Sound' : 'Play Soundscape'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Lighting Controller */}
        <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-4">
          <h3 className="text-sm font-bold flex items-center gap-2 text-amber-300">
            <Sun className="w-4 h-4" /> Interactive Lighting Presets
          </h3>
          <div className="space-y-2">
            {presets.map((p) => (
              <button
                key={p.id}
                onClick={() => setLightPreset(p.id as any)}
                className={`w-full p-3 rounded-xl border font-bold text-xs text-left transition flex items-center justify-between ${
                  lightPreset === p.id ? p.bg : 'bg-slate-900 text-slate-300 border-slate-800 hover:border-slate-700'
                }`}
              >
                <span>{p.name}</span>
                {lightPreset === p.id && <CheckCircle2 className="w-4 h-4 text-amber-400" />}
              </button>
            ))}
          </div>
        </div>

        {/* Ambient Soundscapes */}
        <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-4">
          <h3 className="text-sm font-bold flex items-center gap-2 text-blue-300">
            <Volume2 className="w-4 h-4" /> Soothing Audio Soundscapes
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {(['ocean', 'rain', 'white_noise', 'forest'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSoundscape(s)}
                className={`p-3 rounded-xl border text-xs font-bold capitalize text-center transition ${
                  soundscape === s ? 'bg-blue-600 text-white border-blue-400' : 'bg-slate-900 text-slate-400 border-slate-800'
                }`}
              >
                {s.replace('_', ' ')}
              </button>
            ))}
          </div>
          <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 text-xs text-slate-400">
            Active Sound Engine: <span className="font-bold text-white capitalize">{soundscape.replace('_', ' ')}</span>
          </div>
        </div>

        {/* Sensory De-escalation Timer */}
        <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-4 flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-bold flex items-center gap-2 text-emerald-300">
              <ShieldAlert className="w-4 h-4" /> De-escalation & Calming Session Timer
            </h3>
            <div className="text-3xl font-black font-mono text-emerald-400 mt-4 text-center">
              {calmingTimer}:00 MIN
            </div>
            <p className="text-xs text-slate-400 text-center mt-1">Recommended timeout for sensory soothing</p>
          </div>

          <div className="flex items-center gap-2">
            <button 
              onClick={() => setCalmingTimer(Math.max(5, calmingTimer - 5))}
              className="flex-1 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-xs font-bold"
            >
              - 5 Min
            </button>
            <button 
              onClick={() => setCalmingTimer(calmingTimer + 5)}
              className="flex-1 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-xs font-bold"
            >
              + 5 Min
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
